import secureLocalStorage from "react-secure-storage";
import {
  clearSecureStorage,
  clearUserContext,
  isUrlWhitelisted,
} from "../helpers";
import DownloadTypeEnum from "../../helpers/Enums/DownloadTypeEnum";

const sendApiRequest = async (
  url,
  method,
  isPrivate,
  body,
  signIn,
  signOut,
  navigate,
  createQueryString,
  userContext,
) => {
  const token = getTokenFromCookie();
  let queryString = "";
  let options = "";

  const headers = isPrivate
    ? {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      }
    : {
        "Content-Type": "application/json",
      };

  if (method === "GET") {
    queryString = "";
    if (createQueryString !== null) {
      queryString = createQueryString({ ...body });
    }
    options = {
      method,
      headers,
    };
  } else if (method === "POST" || method === "DELETE") {
    options = {
      method,
      headers,
      body: JSON.stringify(body),
    };
  }

  const requestUrl = `${process.env.REACT_APP_API_URL}${url}${queryString}`;

  if (isUrlWhitelisted(requestUrl)) {
    try {
      const response = await fetch(requestUrl, options);

      if (!response.ok) {
        if (response.status === 401) {
          if (typeof signOut === "function") {
            clearSecureStorage();
            if (userContext !== undefined) {
              clearUserContext(userContext);
            }
            signOut();
            navigate("/");
          }
          return;
        } else {
          throw (await response.json()).errors;
        }
      }

      if (signIn === null && typeof signOut === "function") {
        clearSecureStorage();
        if (userContext !== undefined) {
          clearUserContext(userContext);
        }
        signOut();
        navigate("/");
      }

      const string = await response.text();
      const data = string === "" ? {} : JSON.parse(string);

      if (data.userId !== undefined) {
        secureLocalStorage.setItem("userId", data.userId);
      }

      if (data.email !== undefined) {
        secureLocalStorage.setItem("email", data.email);
      }

      if (data.isAdmin !== undefined) {
        secureLocalStorage.setItem("isAdmin", data.isAdmin);
      }

      if (data.userRoles !== undefined) {
        secureLocalStorage.setItem("userRoles", data.userRoles);
      }

      if (data.selectedClient !== undefined && data.selectedClient !== null) {
        secureLocalStorage.setItem("selectedClient", data.selectedClient);
      }

      if (data.twoFactorAuthTokenTimeValidity !== undefined) {
        secureLocalStorage.setItem(
          "twoFactorAuthTokenTimeValidity",
          data.twoFactorAuthTokenTimeValidity,
        );
      }

      if (
        (response.headers.get("X-Auth-Token") !== null ||
          (data.token !== undefined && data.token !== "")) &&
        typeof signIn === "function"
      ) {
        if (
          signIn({
            token:
              data.token !== undefined
                ? data.token
                : response.headers.get("X-Auth-Token"),
            tokenType: "Bearer",
            expiresIn: Number(process.env.REACT_APP_TOKEN_EXPIRATION_TIME),
            authState: url,
          })
        ) {
          if (userContext !== null && userContext !== undefined) {
            userContext.setTwoFactorAuthResendToken(null);
          }
          if (signOut === null && typeof navigate === "function") {
            const userRoles = secureLocalStorage.getItem("userRoles");
            if (
              userRoles !== null &&
              userRoles !== undefined &&
              userRoles.includes("PASectorManager")
            ) {
              navigate("/authModule/technicalFlows");
            } else if (
              userRoles !== null &&
              userRoles !== undefined &&
              userRoles.includes("PAEconomicalManager")
            ) {
              navigate("/authModule/economicalFlows");
            } else if (
              userRoles !== null &&
              userRoles !== undefined &&
              userRoles.includes("PAUser")
            ) {
              navigate("/");
            } else if (
              userRoles !== null &&
              userRoles !== undefined &&
              userRoles.includes("PAAdmin")
            ) {
              navigate("/users");
            } else if (
              userRoles !== null &&
              userRoles !== undefined &&
              userRoles.includes("Administrator")
            ) {
              navigate("/");
            }
          }
        } else {
          throw new Error(["Something went wrong!"]);
        }
      }

      return { data, errors: null };
    } catch (error) {
      return { data: null, errors: error };
    }
  }
};

const getTokenFromCookie = () => {
  const cookie = document.cookie;
  const cookieParts = cookie.split(";");
  let token = "";
  cookieParts.forEach((part) => {
    const key = part.split("=")[0];
    const value = part.split("=")[1];

    if (key.trim() === "_auth") {
      token = value;
    }
  });

  return token;
};

const createQueryStringFromFilters = ({
  fiscalCode,
  codes,
  fromDate,
  toDate,
  flowStatuses,
  skip,
  take,
}) => {
  const queryString = `?FiscalCode=${fiscalCode}${generateQueryStingFromArray(
    "Codes",
    codes,
  )}&FromDate=${fromDate}&ToDate=${toDate}${generateQueryStingFromArray(
    "Statuses",
    flowStatuses,
  )}${skip !== undefined ? `&Skip=${skip}` : ""}${take !== undefined ? `&Take=${take}` : ""}`;

  return queryString;
};

const createQueryString = ({ fiscalCode, flowId }) => {
  let queryString = "";
  if (flowId !== null && fiscalCode !== null) {
    queryString = `?FiscalCode=${fiscalCode}&FlowId=${flowId}`;
  } else if (flowId === null) {
    queryString = `?FiscalCode=${fiscalCode}`;
  } else if (fiscalCode === null) {
    queryString = `?FlowId=${flowId}`;
  }

  return queryString;
};

const createClientIdQueryString = ({ clientId }) => {
  if (clientId !== null && clientId !== undefined) {
    return `?clientId=${clientId}`;
  }
};

const createClientQueryString = ({ clientFiscalCode }) => {
  return `?ClientFiscalCode=${clientFiscalCode}`;
};

const createFilterReadyFlowsQueryString = ({ fiscalCode, codes }) => {
  let filterText = "";
  if (codes !== null) {
    codes.forEach((code) => {
      filterText += `&codes=${code}`;
    });
  }
  return `?fiscalCode=${fiscalCode}${filterText}`;
};

const createScrollQueryString = ({
  flowId,
  searchText,
  sortColumn,
  isAscending,
  skip,
  take,
}) => {
  return `?flowId=${flowId}&searchText=${searchText}&sortColumn=${sortColumn}&isAscending=${isAscending}&skip=${skip}&take=${take}`;
};

const generateQueryStingFromArray = (queryParam, dataArray) => {
  let queryString = "";
  if (dataArray !== null && dataArray !== undefined) {
    if (dataArray[0] !== null) {
      if (dataArray.length > 0) {
        dataArray.map((item) => (queryString += `&${queryParam}=${item}`));
      }
      return queryString;
    } else {
      return "";
    }
  } else {
    return "";
  }
};

const downloadFile = async (data, signIn, signOut, navigate, userContext) => {
  const token = getTokenFromCookie();
  let url = "";
  switch (data.downloadType) {
    case DownloadTypeEnum.FlowResult:
      url = `${process.env.REACT_APP_API_URL}Flow/DownloadFlowResultFile?FiscalCode=${data.fiscalCode}&Code=${data.clientCode}&FileName=${data.fileName}`;
      break;
    case DownloadTypeEnum.DocumentCheckResult:
      url = `${process.env.REACT_APP_API_URL}DocumentCheck/GetDocumentCheckMergedResults?DocumentCheckResultId=${data.fileResultId}`;
      break;
    case DownloadTypeEnum.ExternalFlowZip:
      url = `${process.env.REACT_APP_API_URL}ExternalFlow/DownloadZip?ExternalFlowId=${data.flowId}`;
      break;
    case DownloadTypeEnum.ExternalFlowZipForSign:
      url = `${process.env.REACT_APP_API_URL}ExternalFlow/DownloadZipForSignature?ExternalFlowId=${data.flowId}`;
      break;
    case DownloadTypeEnum.ExternalFlowZipWithSign:
      url = `${process.env.REACT_APP_API_URL}ExternalFlow/DownloadZipWithSignature?ExternalFlowId=${data.flowId}`;
      break;
    default:
      alert("No");
  }

  if (isUrlWhitelisted(url)) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (typeof signOut === "function") {
            clearSecureStorage();
            if (userContext !== undefined) {
              clearUserContext(userContext);
            }
            signOut();
            navigate("/");
          }
          return;
        } else {
          throw new Error((await response.json()).errors);
        }
      }

      if (
        response.headers.get("X-Auth-Token") !== null &&
        typeof signIn === "function"
      ) {
        if (
          signIn({
            token: response.headers.get("X-Auth-Token"),
            tokenType: "Bearer",
            expiresIn: Number(process.env.REACT_APP_TOKEN_EXPIRATION_TIME),
            authState: "Download",
          })
        ) {
        } else {
          throw new Error(["Something went wrong!"]);
        }
      }

      if (response.headers.get("Content-Disposition") !== null) {
        const filename = response.headers
          .get("Content-Disposition")
          .split(";")
          .find((el) => el.includes("filename"))
          .replace("filename=", "")
          .replace(/["']/g, "")
          .trim();
        secureLocalStorage.setItem("fileName", filename);
      }

      return response;
    } catch (error) {
      return { errors: error };
    }
  }
};

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

const uploadFile = async (file, data, endpoint, uploadCompleted) => {
  let formData = new FormData();
  // Append the file to the FormData object
  formData.append("FileName", encodeURI(file.name));
  formData.append("FileSize", file.size);
  formData.append("FileContent", (await fileToBase64(file)).split(",")[1]);

  for (const key in data) {
    // Ensure it's a direct property, not inherited
    if (data.hasOwnProperty(key)) {
      formData.append(key, data[key]);
    }
  }

  const token = getTokenFromCookie();

  // Create a new XMLHttpRequest object
  const xhr = new XMLHttpRequest();

  // Set up the request
  xhr.open("POST", `${process.env.REACT_APP_API_URL}${endpoint}`, true);

  // Set the content type header
  xhr.setRequestHeader("Authorization", "Bearer " + token);

  // Handle the response
  xhr.onload = function () {
    if (xhr.status === 200) {
      if (typeof uploadCompleted === "function") {
        uploadCompleted({ data: xhr.response, errors: null });
      }
    } else {
      // File upload failed
      uploadCompleted({
        data: null,
        errors:
          xhr.response !== ""
            ? JSON.parse(xhr.response).errors
            : [`${xhr.status} (${xhr.statusText})`],
      });
    }
  };
  // Send the request
  xhr.send(formData);
};

const getLogo = async (
  { fiscalCode },
  signIn,
  signOut,
  navigate,
  userContext,
) => {
  const token = getTokenFromCookie();

  const url = `${process.env.REACT_APP_API_URL}Client/GetClientLogo?FiscalCode=${fiscalCode}`;

  if (isUrlWhitelisted(url)) {
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          if (typeof signOut === "function") {
            clearSecureStorage();
            if (userContext !== undefined) {
              clearUserContext(userContext);
            }
            signOut();
            navigate("/");
          }
          return;
        } else {
          throw new Error((await response.json()).errors);
        }
      }

      if (
        response.headers.get("X-Auth-Token") !== null &&
        typeof signIn === "function"
      ) {
        if (
          signIn({
            token: response.headers.get("X-Auth-Token"),
            tokenType: "Bearer",
            expiresIn: Number(process.env.REACT_APP_TOKEN_EXPIRATION_TIME),
            authState: "Logo",
          })
        ) {
        } else {
          throw new Error(["Something went wrong!"]);
        }
      }

      return response;
    } catch (error) {
      return { errors: error };
    }
  }
};

const transfromDate = (date) => {
  return `${date.getFullYear()}-${
    date.getMonth() + 1 < 10 ? "0" + (date.getMonth() + 1) : date.getMonth() + 1
  }-${date.getDate() < 10 ? "0" + date.getDate() : date.getDate()}`;
};

export {
  sendApiRequest,
  createQueryStringFromFilters,
  createQueryString,
  createClientQueryString,
  createClientIdQueryString,
  createScrollQueryString,
  createFilterReadyFlowsQueryString,
  downloadFile,
  uploadFile,
  getLogo,
  transfromDate,
  getTokenFromCookie,
};
